import { PLACEHOLDER_VALUE } from '@/constants';
import { InstrumentRunDataRow } from '@/types';
import type { SelectionOption } from '@/types/autocomplete';
import { AutoComplete, Button, CheckboxGroup, Dialog, Stack } from '@nautilusbio/nb-design-system';
import type { ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import React, { type FC, type KeyboardEvent, useCallback, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './SelectionCellRenderer.module.css';

// State type for the reducer
interface EditingState {
  isEditing: boolean;
  searchQuery: string;
  selectedKey: string | null;
  applyToEmpty: boolean;
}

// Action types for the reducer
type EditingAction =
  | {
      type: 'START_EDITING';
      payload: { searchQuery: string; selectedKey: string | null };
    }
  | { type: 'UPDATE_SEARCH'; payload: string }
  | { type: 'UPDATE_SELECTION'; payload: string | null }
  | { type: 'TOGGLE_APPLY_TO_EMPTY'; payload: boolean }
  | { type: 'CANCEL_EDITING' }
  | { type: 'COMPLETE_EDITING' };

// Initial state
const initialState: EditingState = {
  isEditing: false,
  searchQuery: '',
  selectedKey: null,
  applyToEmpty: false,
};

// Reducer function
function editingReducer(state: EditingState, action: EditingAction): EditingState {
  switch (action.type) {
    case 'START_EDITING':
      return {
        ...initialState,
        isEditing: true,
        searchQuery: action.payload.searchQuery,
        selectedKey: action.payload.selectedKey,
      };
    case 'UPDATE_SEARCH':
      return {
        ...state,
        searchQuery: action.payload,
      };
    case 'UPDATE_SELECTION':
      return {
        ...state,
        selectedKey: action.payload,
      };
    case 'TOGGLE_APPLY_TO_EMPTY':
      return {
        ...state,
        applyToEmpty: action.payload,
      };
    case 'CANCEL_EDITING':
      return initialState;
    case 'COMPLETE_EDITING':
      return initialState;
    default:
      return state;
  }
}

export interface SelectionCellRendererProps extends ICellRendererParams<InstrumentRunDataRow> {
  // Field name to update (e.g., 'study_name', 'org_id')
  fieldName: keyof InstrumentRunDataRow;
  // Options for the autocomplete
  options: SelectionOption[];
  // Translation keys
  placeholderKey: string;
  dialogTitleKey: string;
  applyToEmptyKey: string;
  emptyHintKey: string;
  // Optional custom display value formatter
  formatDisplayValue?: (value: string | number | null | undefined) => string;
  // Determines what value to save: 'key' saves the option.key (e.g., ID), 'label' saves the option.label (e.g., name)
  saveValue?: 'key' | 'label';
}

/**
 * Reusable cell renderer for selection with Dialog interface and bulk update support
 * Handles both display and edit modes without using AG Grid's popup editors
 */
export const SelectionCellRenderer: FC<SelectionCellRendererProps> = ({
  value: cellValue,
  data,
  api,
  node,
  column,
  fieldName,
  options,
  placeholderKey,
  dialogTitleKey,
  applyToEmptyKey,
  emptyHintKey,
  formatDisplayValue,
  saveValue = 'label', // Default to 'label' for backwards compatibility
}) => {
  const { t } = useTranslation('sampleManager');
  const [state, dispatch] = useReducer(editingReducer, initialState);
  const editContainerRef = useRef<HTMLDivElement>(null);
  const displayButtonRef = useRef<HTMLButtonElement>(null);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!state.searchQuery) return options;
    const query = state.searchQuery.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, state.searchQuery]);

  // Handle single-click to enter edit mode
  const handleClick = useCallback(() => {
    // Initialize with empty state so AutoComplete is ready for typing
    dispatch({
      type: 'START_EDITING',
      payload: {
        searchQuery: '',
        selectedKey: null,
      },
    });
  }, []);

  // Handle selection change (just update state, don't save yet)
  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      dispatch({
        type: 'UPDATE_SELECTION',
        payload: key !== null ? String(key) : null,
      });

      // Update searchQuery to show the selected option's label in the input
      if (key !== null) {
        const selectedOption = options.find(opt => opt.key === String(key));
        if (selectedOption) {
          dispatch({ type: 'UPDATE_SEARCH', payload: selectedOption.label });
        }
      }
    },
    [options],
  );

  // Handle checkbox change
  const handleApplyToEmptyChange = useCallback((values: string[]) => {
    dispatch({
      type: 'TOGGLE_APPLY_TO_EMPTY',
      payload: values.includes('apply-to-empty'),
    });
  }, []);

  // Handle save button click
  const handleSave = useCallback(() => {
    if (state.selectedKey !== null && api) {
      const selectedOption = options.find(opt => opt.key === state.selectedKey);

      if (selectedOption) {
        // Determine what value to save based on saveValue prop
        const valueToSave = saveValue === 'key' ? selectedOption.key : selectedOption.label;

        // If "Apply to all empty" is checked, update all rows with empty values
        if (state.applyToEmpty) {
          const allRowNodes: IRowNode<InstrumentRunDataRow>[] = [];
          api.forEachNode(rowNode => {
            if (rowNode.data) {
              const fieldValue = rowNode.data[fieldName];
              const isEmpty = !fieldValue || fieldValue === PLACEHOLDER_VALUE || fieldValue === '';
              if (isEmpty) {
                (rowNode.data[fieldName] as string) = valueToSave;
                allRowNodes.push(rowNode);
              }
            }
          });

          // Trigger value changed events for all updated rows
          if (allRowNodes.length > 0) {
            allRowNodes.forEach(rowNode => {
              const event = {
                api,
                node: rowNode,
                data: rowNode.data,
                column,
                colDef: column?.getColDef(),
                oldValue: '',
                newValue: valueToSave,
                rowIndex: rowNode.rowIndex,
                type: 'cellValueChanged',
              };
              api.dispatchEvent(event);
            });
          }
        } else if (data && node && column) {
          // Only update current cell
          (data[fieldName] as string) = valueToSave;

          // Trigger value changed event
          const event = {
            api,
            node,
            data,
            column,
            colDef: column.getColDef(),
            oldValue: cellValue,
            newValue: valueToSave,
            rowIndex: node.rowIndex,
            type: 'cellValueChanged',
          };
          api.dispatchEvent(event);
        }
      }
    }

    // Exit edit mode
    dispatch({ type: 'COMPLETE_EDITING' });
  }, [state.selectedKey, state.applyToEmpty, options, api, column, data, node, cellValue, fieldName, saveValue]);

  // Handle escape key to cancel editing
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      dispatch({ type: 'CANCEL_EDITING' });
    }
  }, []);

  // Handle cancel button click
  const handleCancel = useCallback(() => {
    dispatch({ type: 'CANCEL_EDITING' });
  }, []);

  // Format display value (ensure string output)
  const displayValue = formatDisplayValue
    ? formatDisplayValue(cellValue)
    : cellValue === null || cellValue === undefined
      ? ''
      : String(cellValue);

  // Render edit mode
  if (state.isEditing) {
    return (
      <div ref={editContainerRef} className={styles.editContainer} onKeyDown={handleKeyDown} role="group">
        <Dialog
          classNames={{ dialog: styles.dialog }}
          content={
            <Stack fullWidth gap={3} orientation="vertical">
              <AutoComplete
                containerClassName={styles.autoCompleteContainer}
                data-testid={`${fieldName}-autocomplete`}
                inputProps={{ autoFocus: true }}
                menuTrigger="focus"
                onInputChange={(searchValue: string) => dispatch({ type: 'UPDATE_SEARCH', payload: searchValue })}
                onSelectionChange={handleSelectionChange}
                options={filteredOptions}
                placeholder={t(placeholderKey, { defaultValue: 'Search...' })}
                root={document.body}
                selectedKey={state.selectedKey}
                shouldFlip={true}
                size="sm"
                value={state.searchQuery}
              />
              <CheckboxGroup
                data-testid={`${fieldName}-apply-to-empty-checkbox`}
                id={`${fieldName}-bulk-apply-${node?.rowIndex ?? 0}`}
                onChange={handleApplyToEmptyChange}
                options={[
                  {
                    value: 'apply-to-empty',
                    name: 'apply-to-empty',
                    children: t(applyToEmptyKey, {
                      defaultValue: 'Apply to all empty rows',
                    }),
                  },
                ]}
                value={state.applyToEmpty ? ['apply-to-empty'] : []}
              />
            </Stack>
          }
          data-testid={`${fieldName}-${node?.rowIndex ?? 0}-dialog`}
          floatingProps={{
            open: state.isEditing,
            onOpenChange: (open: boolean) => {
              if (!open) {
                handleCancel();
              }
            },
          }}
          footer={
            <Stack
              alignItems="center"
              className={styles.footer}
              fullWidth
              gap={4}
              justifyContent="center"
              orientation="horizontal"
            >
              <Button data-testid={`${fieldName}-cancel-btn`} onPress={handleCancel} size="lg" visual="outline">
                {t('cancel', { ns: 'general' })}
              </Button>
              <Button
                data-testid={`${fieldName}-save-btn`}
                isDisabled={!state.selectedKey}
                onPress={handleSave}
                size="lg"
                visual="primary"
              >
                {t('save', { ns: 'general' })}
              </Button>
            </Stack>
          }
          title={t(dialogTitleKey, { defaultValue: 'Select' })}
          triggerProps={{
            children: t(dialogTitleKey, { defaultValue: 'Select' }),
          }}
        />
      </div>
    );
  }

  // Render display mode
  return (
    <Button
      className={styles.displayContainer}
      data-testid={`${fieldName}-${node?.rowIndex ?? 0}-display`}
      fullWidth
      onPress={handleClick}
      ref={displayButtonRef}
      visual="unstyled"
      {...{
        title:
          displayValue && displayValue !== PLACEHOLDER_VALUE
            ? displayValue
            : t(emptyHintKey, { defaultValue: 'Click to select' }),
      }}
    >
      {displayValue && displayValue !== PLACEHOLDER_VALUE ? (
        <span className={styles.displayValue}>{displayValue}</span>
      ) : (
        t(emptyHintKey, { defaultValue: 'Click to select' })
      )}
    </Button>
  );
};

SelectionCellRenderer.displayName = 'SelectionCellRenderer';

export default SelectionCellRenderer;
